import React from 'react';
import styled from 'styled-components';
import { TokenSet } from '../../styles/tokens';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const Bar = styled.nav<{ tokens: TokenSet }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  background: ${p => p.tokens.bgSurface};
  border-bottom: 1px solid ${p => p.tokens.border};
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
`;

const Left = styled.div<{ clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: ${p => p.clickable ? 'pointer' : 'default'};
  user-select: none;
`;

const LogoImg = styled.img`
  width: 36px;
  height: 36px;
  display: block;
`;

const LogoText = styled.span<{ color: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.color};
  letter-spacing: 1.5px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const NavLangToggle = styled.button<{ tokens: TokenSet }>`
  background: none;
  border: 1px solid ${p => p.tokens.border};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.tokens.textMuted};
  padding: 4px 10px;
  letter-spacing: 0.5px;
  height: 32px;
  &:hover { background: ${p => p.tokens.bgElevated}; }
`;

export const NavIconToggle = styled.button<{ tokens: TokenSet }>`
  background: none;
  border: 1px solid ${p => p.tokens.border};
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  color: ${p => p.tokens.textMuted};
  padding: 4px 10px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${p => p.tokens.bgElevated}; }
`;

interface NavbarProps {
  tokens: TokenSet;
  onLogoClick?: () => void;
  rightContent?: React.ReactNode;
  showLanguageToggle?: boolean;
  showThemeToggle?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  tokens,
  onLogoClick,
  rightContent,
  showLanguageToggle = true,
  showThemeToggle = true,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { language, t, toggleLanguage } = useLanguage();

  return (
    <Bar tokens={tokens}>
      <Left clickable={!!onLogoClick} onClick={onLogoClick}>
        <LogoImg src="/favicon.svg" alt="P4D" />
        <LogoText color={tokens.text}>{t.appName}</LogoText>
      </Left>
      <Right>
        {showLanguageToggle && (
          <NavLangToggle tokens={tokens} onClick={toggleLanguage} title={language === 'en' ? 'Português' : 'English'}>
            {language === 'en' ? 'PT' : 'EN'}
          </NavLangToggle>
        )}
        {showThemeToggle && (
          <NavIconToggle tokens={tokens} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            {theme === 'dark' ? '☀' : '☾'}
          </NavIconToggle>
        )}
        {rightContent}
      </Right>
    </Bar>
  );
};
